import { useMemo, useState } from 'react'
import { PlusIcon } from '@phosphor-icons/react'
import {
    ScoopButton,
    ScoopColumn,
    ScoopDialog,
    ScoopRow,
    ScoopSelector,
    ScoopText,
    ScoopTextField,
} from '../../../../ui'
import { ActionCandidateCard } from './ActionCandidateCard'
import { ReportState } from '../ReportState/ReportState'
import { getActionScopeGroup } from '../../utils/hierarchyUtils'
import styles from './ActiveActionsPanel.module.scss'

const DEFAULT_FORM = {
    title: '',
    summary: '',
    priority: 'MEDIUM',
    scopeType: 'portfolio',
    scopeNodeId: '',
    scopeEntityId: '',
    scopeLevelName: '',
}

const DEFAULT_SCOPE_OPTIONS = [
    { label: 'Portfolio', value: 'portfolio', levelName: '' },
    { label: 'Hierarchy Node', value: 'hierarchy_node', levelName: '' },
    { label: 'Entity', value: 'entity', levelName: '' },
]

const buildDefaultForm = (scopeOptions) => {
    const preferredScope =
        scopeOptions.find((option) => option.value === 'hierarchy_node') || scopeOptions[0]
    return {
        ...DEFAULT_FORM,
        scopeType: preferredScope?.value || 'portfolio',
        scopeLevelName: preferredScope?.levelName || '',
    }
}

const unwrapItems = (value) => {
    if (!value) return []
    if (Array.isArray(value)) return value
    if (Array.isArray(value.items)) return value.items
    return []
}

const formatJson = (value) => JSON.stringify(value || {}, null, 2)

export const ActiveActionsPanel = ({
    candidates = [],
    counts,
    loading,
    error,
    onAccept,
    onReject,
    onCreate,
    accepting,
    rejecting,
    creating,
    pendingFingerprint,
    scopeOptions = [],
}) => {
    const [selectedCandidate, setSelectedCandidate] = useState(null)
    const [rejectCandidate, setRejectCandidate] = useState(null)
    const [rejectReason, setRejectReason] = useState('')
    const [showAddDialog, setShowAddDialog] = useState(false)
    const [form, setForm] = useState(DEFAULT_FORM)

    const resolvedScopeOptions = useMemo(() => {
        const options = scopeOptions.length > 0 ? scopeOptions : DEFAULT_SCOPE_OPTIONS
        const hasPortfolio = options.some((option) => option.value === 'portfolio')
        return hasPortfolio ? options : [DEFAULT_SCOPE_OPTIONS[0], ...options]
    }, [scopeOptions])

    const defaultForm = useMemo(
        () => buildDefaultForm(resolvedScopeOptions),
        [resolvedScopeOptions]
    )

    const sortedCandidates = useMemo(() => {
        return [...candidates].sort((a, b) => {
            const statusRank = (candidate) => (candidate.status === 'pending' ? 0 : 1)
            const priorityRank = (candidate) => {
                if (candidate.priority === 'HIGH') return 0
                if (candidate.priority === 'MEDIUM') return 1
                if (candidate.priority === 'LOW') return 2
                return 3
            }
            const byStatus = statusRank(a) - statusRank(b)
            if (byStatus !== 0) return byStatus
            const byPriority = priorityRank(a) - priorityRank(b)
            if (byPriority !== 0) return byPriority
            return (b.lastSeenAt || 0) - (a.lastSeenAt || 0)
        })
    }, [candidates])

    const groupedCandidates = useMemo(() => {
        const groups = []
        const groupMap = new Map()
        const groupOrder = ['Portfolio', 'Unscoped']

        sortedCandidates.forEach((candidate) => {
            const groupName = getActionScopeGroup(candidate)
            if (!groupMap.has(groupName)) {
                const group = { name: groupName, candidates: [] }
                groupMap.set(groupName, group)
                groups.push(group)
            }
            groupMap.get(groupName).candidates.push(candidate)
        })

        return groups.sort((a, b) => {
            const aIndex = groupOrder.includes(a.name)
                ? groupOrder.indexOf(a.name)
                : groupOrder.length
            const bIndex = groupOrder.includes(b.name)
                ? groupOrder.indexOf(b.name)
                : groupOrder.length
            if (aIndex !== bIndex) return aIndex - bIndex
            return a.name.localeCompare(b.name)
        })
    }, [sortedCandidates])

    const updateForm = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }))
    }

    const handleScopePresetChange = (value) => {
        const option = resolvedScopeOptions.find((item) => item.label === value)
        if (!option) return

        setForm((prev) => ({
            ...prev,
            scopeType: option.value,
            scopeLevelName: option.levelName,
            scopeNodeId: option.value === 'hierarchy_node' ? prev.scopeNodeId : '',
            scopeEntityId: option.value === 'entity' ? prev.scopeEntityId : '',
        }))
    }

    const handleCreate = () => {
        const title = form.title.trim()
        if (!title) return
        const scopeType = form.scopeType
        const scopeNodeId = form.scopeNodeId.trim()
        const scopeEntityId = form.scopeEntityId.trim()
        if (scopeType === 'hierarchy_node' && (!scopeNodeId || !form.scopeLevelName.trim())) return
        if (scopeType === 'entity' && !scopeEntityId) return

        onCreate?.({
            title,
            summary: form.summary.trim(),
            priority: form.priority,
            scopeType,
            scopeNodeId: scopeType === 'hierarchy_node' ? scopeNodeId : '',
            scopeEntityId: scopeType === 'entity' ? scopeEntityId : '',
            scopeLevelName: form.scopeLevelName.trim(),
        })
        setForm(defaultForm)
        setShowAddDialog(false)
    }

    const handleReject = () => {
        if (!rejectCandidate) return
        onReject?.(rejectCandidate, rejectReason.trim())
        setRejectCandidate(null)
        setRejectReason('')
    }

    if (loading) {
        return <ReportState title="Loading actions…" />
    }

    if (error) {
        return (
            <ReportState
                variant="error"
                title="Unable to load actions"
                message={error.message}
            />
        )
    }

    const selectedGuideRefs = unwrapItems(selectedCandidate?.guideRefs)
    const selectedEvidence = unwrapItems(selectedCandidate?.evidence)
    const canCreateAction = Boolean(
        form.title.trim() &&
            (form.scopeType === 'portfolio' ||
                (form.scopeType === 'hierarchy_node' &&
                    form.scopeLevelName.trim() &&
                    form.scopeNodeId.trim()) ||
                (form.scopeType === 'entity' && form.scopeEntityId.trim()))
    )

    const handleOpenAddDialog = () => {
        setForm(defaultForm)
        setShowAddDialog(true)
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <ScoopColumn className={styles.headerText}>
                    <ScoopText className={styles.title}>Active Actions</ScoopText>
                    <ScoopText className={styles.subtitle}>
                        {counts?.total || sortedCandidates.length} active · {counts?.pending || 0}{' '}
                        pending · {counts?.accepted || 0} accepted
                    </ScoopText>
                </ScoopColumn>
                <ScoopButton variant="default" size="sm" onClick={handleOpenAddDialog}>
                    <PlusIcon size={16} weight="bold" />
                    Add Action
                </ScoopButton>
            </div>

            {sortedCandidates.length === 0 ? (
                <ReportState
                    title="No active actions"
                    message="Generated and accepted actions will appear here — or add one now."
                    action={
                        <ScoopButton variant="default" size="sm" onClick={handleOpenAddDialog}>
                            <PlusIcon size={16} weight="bold" />
                            Add Action
                        </ScoopButton>
                    }
                />
            ) : (
                <div className={styles.actionList}>
                    {groupedCandidates.map((group) => (
                        <div key={group.name} className={styles.scopeGroup}>
                            <div className={styles.scopeGroupHeader}>
                                <ScoopText className={styles.scopeGroupTitle}>
                                    {group.name}
                                </ScoopText>
                                <ScoopText className={styles.scopeGroupCount}>
                                    {group.candidates.length}
                                </ScoopText>
                            </div>
                            <div className={styles.scopeGroupList}>
                                {group.candidates.map((candidate) => (
                                    <ActionCandidateCard
                                        key={candidate.candidateFingerprint}
                                        candidate={candidate}
                                        onAccept={onAccept}
                                        onReject={(item) => setRejectCandidate(item)}
                                        onViewDetails={setSelectedCandidate}
                                        accepting={accepting}
                                        rejecting={rejecting}
                                        pendingFingerprint={pendingFingerprint}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ScoopDialog
                open={showAddDialog}
                onClose={() => setShowAddDialog(false)}
                title="Add Action"
                maxWidth="md"
                actions={
                    <>
                        <ScoopButton variant="text" onClick={() => setShowAddDialog(false)}>
                            Cancel
                        </ScoopButton>
                        <ScoopButton
                            variant="default"
                            onClick={handleCreate}
                            disabled={!canCreateAction}
                            loading={creating}
                        >
                            Add
                        </ScoopButton>
                    </>
                }
            >
                <ScoopColumn className={styles.form}>
                    <ScoopTextField
                        label="Title"
                        value={form.title}
                        onChange={(event) => updateForm('title', event.target.value)}
                        autoFocus
                    />
                    <ScoopTextField
                        label="Summary"
                        value={form.summary}
                        onChange={(event) => updateForm('summary', event.target.value)}
                        multiline
                        rows={4}
                    />
                    <ScoopRow className={styles.formRow}>
                        <ScoopSelector
                            label="Priority"
                            value={form.priority}
                            onChange={(event) => updateForm('priority', event.target.value)}
                            options={[
                                { label: 'High', value: 'HIGH' },
                                { label: 'Medium', value: 'MEDIUM' },
                                { label: 'Low', value: 'LOW' },
                            ]}
                        />
                        <ScoopSelector
                            label="Scope"
                            value={
                                resolvedScopeOptions.find(
                                    (item) =>
                                        item.value === form.scopeType &&
                                        item.levelName === form.scopeLevelName
                                )?.label ||
                                resolvedScopeOptions[0]?.label ||
                                'Portfolio'
                            }
                            onChange={(event) => handleScopePresetChange(event.target.value)}
                            options={resolvedScopeOptions.map((option) => ({
                                label: option.label,
                                value: option.label,
                            }))}
                        />
                    </ScoopRow>
                    {form.scopeType === 'hierarchy_node' && (
                        <>
                            <ScoopTextField
                                label="Hierarchy level"
                                value={form.scopeLevelName}
                                onChange={(event) =>
                                    updateForm('scopeLevelName', event.target.value)
                                }
                                placeholder="Configured hierarchy level"
                            />
                            <ScoopTextField
                                label={`${form.scopeLevelName || 'Hierarchy node'} ID`}
                                value={form.scopeNodeId}
                                onChange={(event) => updateForm('scopeNodeId', event.target.value)}
                                placeholder="Node ID"
                            />
                        </>
                    )}
                    {form.scopeType === 'entity' && (
                        <ScoopTextField
                            label={`${form.scopeLevelName || 'Entity'} ID`}
                            value={form.scopeEntityId}
                            onChange={(event) => updateForm('scopeEntityId', event.target.value)}
                            placeholder="Entity ID"
                        />
                    )}
                </ScoopColumn>
            </ScoopDialog>

            <ScoopDialog
                open={Boolean(rejectCandidate)}
                onClose={() => {
                    setRejectCandidate(null)
                    setRejectReason('')
                }}
                title="Reject Action"
                maxWidth="sm"
                actions={
                    <>
                        <ScoopButton
                            variant="text"
                            onClick={() => {
                                setRejectCandidate(null)
                                setRejectReason('')
                            }}
                        >
                            Cancel
                        </ScoopButton>
                        <ScoopButton variant="danger" onClick={handleReject} loading={rejecting}>
                            Reject
                        </ScoopButton>
                    </>
                }
            >
                <ScoopColumn className={styles.form}>
                    <ScoopText className={styles.dialogText}>{rejectCandidate?.title}</ScoopText>
                    <ScoopTextField
                        label="Reason"
                        value={rejectReason}
                        onChange={(event) => setRejectReason(event.target.value)}
                        multiline
                        rows={3}
                    />
                </ScoopColumn>
            </ScoopDialog>

            <ScoopDialog
                open={Boolean(selectedCandidate)}
                onClose={() => setSelectedCandidate(null)}
                title="Action Details"
                maxWidth="lg"
            >
                <ScoopColumn className={styles.details}>
                    <ScoopText className={styles.detailTitle}>{selectedCandidate?.title}</ScoopText>
                    {selectedCandidate?.summary && (
                        <ScoopText className={styles.dialogText}>
                            {selectedCandidate.summary}
                        </ScoopText>
                    )}
                    <div className={styles.detailSection}>
                        <ScoopText className={styles.detailSectionTitle}>Catalog Actions</ScoopText>
                        {selectedGuideRefs.length > 0 ? (
                            <ul className={styles.detailList}>
                                {selectedGuideRefs.map((guide, index) => (
                                    <li key={`${guide.guideId || index}`}>
                                        {guide.actionText || guide.whyThisOne || guide.guideId}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <ScoopText className={styles.stateText}>None</ScoopText>
                        )}
                    </div>
                    <div className={styles.detailSection}>
                        <ScoopText className={styles.detailSectionTitle}>Evidence</ScoopText>
                        {selectedEvidence.length > 0 ? (
                            <pre className={styles.jsonBlock}>{formatJson(selectedEvidence)}</pre>
                        ) : (
                            <ScoopText className={styles.stateText}>None</ScoopText>
                        )}
                    </div>
                    <div className={styles.detailSection}>
                        <ScoopText className={styles.detailSectionTitle}>Goals</ScoopText>
                        <pre className={styles.jsonBlock}>
                            {formatJson(selectedCandidate?.goals)}
                        </pre>
                    </div>
                </ScoopColumn>
            </ScoopDialog>
        </div>
    )
}
